import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { TeamNewFormValues, NewReferenceData } from '../types/teamNew.types';

function generateSlug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function getUniqueCode(companyId: string, baseSlug: string): Promise<string> {
  let slug = baseSlug;
  let counter = 2;
  while (true) {
    const { data } = await supabase
      .from('teams')
      .select('id')
      .eq('company_id', companyId)
      .eq('code', slug)
      .maybeSingle();
    if (!data) return slug;
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
}

export const teamNewService = {
  async getReferenceData(companyId: string, userId: string): Promise<NewReferenceData> {
    await permissionEngine.assert(userId, 'teams.view', { companyId });
    
    const [projectsRes, usersRes, deptsRes, zonesRes, contractsRes] = await Promise.all([
      supabase.from('projects').select('id, name, code').eq('company_id', companyId).eq('is_active', true).order('name'),
      supabase.from('user_profiles').select('id, full_name').eq('company_id', companyId).eq('is_active', true).order('full_name'),
      supabase.from('categories').select('id, name, group:category_groups!inner(code)').eq('company_id', companyId).eq('group.code', 'department').eq('is_active', true).order('name'),
      supabase.from('project_zones').select('id, zone_name').eq('company_id', companyId).order('zone_name'),
      supabase.from('construction_contracts').select('id, contract_name, contract_code').eq('company_id', companyId).order('contract_name')
    ]);

    return {
      projects: (projectsRes.data || []).map(p => ({ value: p.id, label: `${p.code} - ${p.name}` })),
      users: (usersRes.data || []).map(u => ({ value: u.id, label: u.full_name })),
      departments: (deptsRes.data || []).map(d => ({ value: d.id, label: d.name })),
      zones: (zonesRes.data || []).map(z => ({ value: z.id, label: z.zone_name })),
      contracts: (contractsRes.data || []).map(c => ({ value: c.id, label: `${c.contract_code} - ${c.contract_name}` }))
    };
  },

  async createTeam(companyId: string, values: TeamNewFormValues, userId: string) {
    if (!companyId) {
      const error = new Error('Không thể tạo team: Thiếu ID công ty (Company ID). Vui lòng tải lại trang.');
      console.error('[teamNewService] Validation failed:', error.message);
      throw error;
    }

    await permissionEngine.assert(userId, 'teams.create', { companyId });
    
    const { members, additional_notes, ...teamData } = values;

    // Combine notes if both exist
    const finalNotes = [teamData.notes, additional_notes].filter(Boolean).join('\n---\n');

    const baseCode = generateSlug(teamData.name);
    const uniqueCode = await getUniqueCode(companyId, baseCode);

    const payload = {
      ...teamData,
      code: uniqueCode,
      notes: finalNotes || null,
      company_id: companyId,
      project_id: teamData.project_id || null,
      contract_id: teamData.contract_id || null,
      zone_id: teamData.zone_id || null,
      department_id: teamData.department_id || null,
      leader_user_id: teamData.leader_user_id || null,
    };

    const { data: team, error: teamError } = await supabase
      .from('teams')
      .insert(payload)
      .select()
      .single();

    if (teamError) throw teamError;

    if (members && members.length > 0) {
      const memberInserts = members.map(memberId => ({
        team_id: team.id,
        user_id: memberId,
        company_id: companyId,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const { error: memberError } = await supabase
        .from('team_members')
        .insert(memberInserts);

      if (memberError) throw memberError;
    }

    return team;
  }
};
